import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  Pagination,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PreviewIcon from "@mui/icons-material/Visibility";
import SportsCricketIcon from "@mui/icons-material/SportsCricket";
import GroupIcon from "@mui/icons-material/Group";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useFormik } from "formik";
import * as yup from "yup";
import { tournamentApi } from "../services/api";
import { SeriesStatus, Series as Tournament } from "../models";

interface TournamentFormValues {
  name: string;
  long_name: string;
  abbreviation: string;
  slug: string;
  start_date: string;
  end_date: string;
  timeline: SeriesStatus;
  match_format?: string;
  type?: string;
  d_id?: number | null;
  c_id?: number | null;
  e_id?: number | null;
  e_o_id?: number | null;
  mt_id?: number | null;
  is_visible: boolean;
  priority: number;
  description: string | null;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface FilterState {
  status: string;
  format: string;
  search: string;
  ordering: string;
}

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  long_name: yup.string().required("Long name is required"),
  start_date: yup.string().required("Start date is required"),
  end_date: yup.string().required("End date is required"),
  timeline: yup.string().required("Status is required"),
  slug: yup.string().required("Slug is required"),
});

const statusOptions = [
  { value: "", label: "All" },
  { value: "UPCOMING", label: "Upcoming" },
  { value: "ONGOING", label: "Ongoing" },
  { value: "PAST", label: "Completed" },
];

const formatOptions = [
  { value: "", label: "All" },
  { value: "TEST", label: "Test" },
  { value: "ODI", label: "ODI" },
  { value: "T20", label: "T20" },
  { value: "T10", label: "T10" },
  { value: "HUNDRED_BALL", label: "Hundred Ball" },
  { value: "SIXES", label: "Sixes" },
  { value: "MULTI", label: "Multi" },
];

const typeOptions = [
  { value: "", label: "All" },
  { value: "INTERNATIONAL", label: "International" },
  { value: "DOMESTIC", label: "Domestic" },
  { value: "LEAGUE", label: "League" },
  { value: "TOURNAMENT", label: "Tournament" },
  { value: "TOURS", label: "Tours" },
  { value: "OTHER", label: "Other" },
];

const Tournaments: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statusParam = queryParams.get("status");

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] =
    useState<Tournament | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<FilterState>({
    status: statusParam || "",
    search: "",
    ordering: "",
    format: "",
  });

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
      sortable: true,
      filterable: true,
      type: "string",
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      filterable: true,
    },
    {
      field: "slug",
      headerName: "Slug",
      width: 200,
      filterable: true,
    },
    {
      field: "e_id",
      headerName: "E ID",
      width: 70,
      sortable: true,
      filterable: true,
      type: "string",
    },
    {
      field: "d_id",
      headerName: "D ID",
      width: 70,
      sortable: true,
      filterable: true,
      type: "string",
    },
    {
      field: "c_id",
      headerName: "C ID",
      width: 70,
      sortable: true,
      filterable: true,
      type: "string",
    },
    {
      field: "mt_name",
      headerName: "Mt Name",
      width: 200,
      filterable: true,
      type: "string",
    },
    // { field: 'abbreviation', headerName: 'Abbr.', width: 100, sortable: true, filterable: true },
    {
      field: "start_date",
      headerName: "Start Date",
      width: 100,
      sortable: true,
      type: "date",
      valueGetter: (params: any) => (params ? new Date(params) : null),
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 100,
      sortable: true,
      type: "date",
      valueGetter: (params: any) => (params ? new Date(params) : null),
    },
    {
      field: "match_format",
      headerName: "Format",
      width: 70,
      sortable: true,
      filterable: true,
      type: "singleSelect",
      valueOptions: formatOptions
        .map((opt) => opt.value)
        .filter((v) => v !== ""),
    },
    // {
    //   field: 'type',
    //   headerName: 'Type',
    //   width: 120,
    //   sortable: true,
    //   filterable: true,
    //   type: 'singleSelect',
    //   valueOptions: typeOptions.map(opt => opt.value).filter(v => v !== ''),
    // },
    {
      field: "timeline",
      headerName: "Status",
      width: 100,
      // sortable: true,
      // filterable: true,
      type: "singleSelect",
      valueOptions: statusOptions
        .map((opt) => opt.value)
        .filter((v) => v !== ""),
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor:
              params.value === "ONGOING"
                ? "#ff9800"
                : params.value === "PAST"
                ? "#4caf50"
                : params.value === "HOLD"
                ? "#f44336"
                : "#2196f3",
            color: "white",
            padding: "2px 6px",
            fontSize: "0.75rem",
            borderRadius: "10px",
            textTransform: "capitalize",
            display: "inline-block",
            minWidth: "60px",
            textAlign: "center",
          }}
        >
          {params.value?.toLowerCase()}
        </Box>
      ),
    },
    {
      field: "is_visible",
      headerName: "V",
      width: 50,
      sortable: true,
      filterable: true,
      type: "boolean",
      renderCell: (params) => (
        <Box
          sx={{
            color: "white",
            backgroundColor: params.value ? "success.main" : "error.main",
            borderRadius: "50%",
            width: 20,
            height: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.7rem",
            fontWeight: "bold",
          }}
        >
          {params.value ? "✓" : "✕"}
        </Box>
      ),
    },
    {
      field: "priority",
      headerName: "P",
      width: 30,
      type: "number",
      sortable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 350,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Matches">
            <IconButton
              color="secondary"
              size="small"
              onClick={() =>
                window.open(`/matches?series=${params.row.id}`, "_self")
              }
            >
              <SportsCricketIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Squad Players">
            <IconButton
              color="success"
              size="small"
              onClick={() =>
                window.open(`/squad-players?series=${params.row.id}`, "_self")
              }
            >
              <GroupIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Preview">
            <IconButton
              color="info"
              size="small"
              onClick={() =>
                window.open(
                  `https://www.ai11.in/series/${params.row.slug}`,
                  "_blank"
                )
              }
            >
              <PreviewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const formik = useFormik<TournamentFormValues>({
    initialValues: {
      name: "",
      long_name: "",
      abbreviation: "",
      slug: "",
      start_date: "",
      end_date: "",
      timeline: "UPCOMING",
      match_format: "",
      type: "",
      d_id: 0,
      c_id: 0,
      e_id: 0,
      mt_id: 0,
      is_visible: true,
      priority: 0,
      description: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedTournament) {
          await tournamentApi.update(selectedTournament.id, values);
        } else {
          await tournamentApi.create(values);
        }
        fetchTournaments();
        handleClose();
      } catch (error) {
        console.error("Error saving tournament:", error);
      }
    },
  });

  const fetchTournaments = React.useCallback(
    async (pageNumber: number = 1, size: number = pageSize) => {
      setLoading(true);
      try {
        const params: any = {
          page: pageNumber,
          page_size: size,
        };        if (filters.status) {
          params.timeline = filters.status;
        }

        if (filters.search) {
          params.search = filters.search;
        }
        
        if (filters.format) {
          params.match_format = filters.format;
        }
        
        if (filters.ordering) {
          params.ordering = filters.ordering;
        }

        const response = await tournamentApi.getAll(params);
        const data = response.data as PaginatedResponse<Tournament>;
        setTournaments(data.results);
        setTotalCount(data.count);
      } catch (error) {
        console.error("Error fetching tournaments:", error);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize]
  );

  // Update filters when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusFromUrl = params.get("status");
    setFilters((prev) => ({
      ...prev,
      status: statusFromUrl || "",
    }));
  }, [location.search]);

  useEffect(() => {
    fetchTournaments(page, pageSize);
  }, [page, pageSize, filters, fetchTournaments]);

  const handleEdit = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    formik.setValues(tournament);
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this tournament?")) {
      try {
        await tournamentApi.delete(id);
        fetchTournaments();
      } catch (error) {
        console.error("Error deleting tournament:", error);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTournament(null);
    formik.resetForm();
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleSortModelChange = (model: any) => {
    if (model.length > 0) {
      const { field, sort } = model[0];
      const ordering = sort === "desc" ? `-${field}` : field;
      setFilters((prev) => ({
        ...prev,
        ordering,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        ordering: "",
      }));
    }
  };

  const handleFilterModelChange = (model: any) => {
    const newFilters = { ...filters };

    if (model.items) {
      model.items.forEach((item: any) => {
        if (item.field === "timeline") {
          newFilters.status = item.value;
        } else if (item.field === 'name') {
          newFilters.search = item.value;
        }
      });
    }

    setFilters(newFilters);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const status = event.target.value;
    
    // Update URL with the status parameter
    const params = new URLSearchParams(location.search);
    if (status) {
      params.set("status", status);
    } else {
      params.delete("status");
    }
    
    // Update browser URL
    const newUrl = `${location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
    
    // Update the filter state
    setFilters(prev => ({
      ...prev,
      status
    }));
  };

  return (
    <Box sx={{ height: "calc(100vh - 320px)", width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h4">Tournaments</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpen(true)}
        >
          Add Tournament
        </Button>
      </Box>
      
      <Box sx={{ display: "flex", mb: 2, gap: 2, alignItems: "center", flexWrap: "wrap" }}>

        <TextField
          label="Search Tournament"
          variant="outlined"
          size="small"
          value={filters.search}
          onChange={(e) => {
            setFilters(prev => ({
              ...prev,
              search: e.target.value
            }));
            setPage(1); // Reset page when search changes
          }}
          sx={{ minWidth: 200 }}
        />
        
        <TextField
          select
          label="Status"
          value={filters.status}
          onChange={handleStatusChange}
          variant="outlined"
          size="small"
          sx={{ minWidth: 150 }}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value || "all"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        
        
        <TextField
          select
          label="Format"
          value={filters.format}
          onChange={(e) => {
            setFilters(prev => ({
              ...prev,
              format: e.target.value
            }));
            setPage(1);
          }}
          variant="outlined"
          size="small"
          sx={{ minWidth: 150 }}
        >
          {formatOptions.map((option) => (
            <MenuItem key={option.value || "all-formats"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <DataGrid
        rows={tournaments}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 50, page: 0 },
          },
          columns: {
            columnVisibilityModel: {
              // d_id: false,
              c_id: false,
              mt_name: false,
            },
          },
          filter: {
            filterModel: {
              items: filters.status
                ? [
                    {
                      field: "timeline",
                      operator: "equals",
                      value: filters.status,
                    },
                  ]
                : [],
            },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        loading={loading}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={handleFilterModelChange}
        onPaginationModelChange={(model) => {
          setPage(model.page + 1);
          setPageSize(model.pageSize);
        }}
        sx={{
          "& .MuiDataGrid-cell": {
            py: 0.5, // Reduce vertical padding in cells
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f5f5f5", // Light gray background for header
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f0f7ff", // Light blue on hover
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "scroll",
          },
        }}
        density="compact"
      />

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={Math.ceil(totalCount / pageSize)}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedTournament ? "Edit Tournament" : "Add Tournament"}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              fullWidth
              margin="normal"
              name="long_name"
              label="Long Name"
              value={formik.values.long_name}
              onChange={formik.handleChange}
              error={
                formik.touched.long_name && Boolean(formik.errors.long_name)
              }
              helperText={formik.touched.long_name && formik.errors.long_name}
            />
            <TextField
              fullWidth
              margin="normal"
              name="abbreviation"
              label="Abbreviation"
              value={formik.values.abbreviation}
              onChange={formik.handleChange}
              error={
                formik.touched.abbreviation &&
                Boolean(formik.errors.abbreviation)
              }
              helperText={
                formik.touched.abbreviation && formik.errors.abbreviation
              }
            />
            <TextField
              fullWidth
              margin="normal"
              name="slug"
              label="Slug"
              value={formik.values.slug}
              onChange={formik.handleChange}
              error={formik.touched.slug && Boolean(formik.errors.slug)}
              helperText={formik.touched.slug && formik.errors.slug}
            />
            <TextField
              fullWidth
              margin="normal"
              name="start_date"
              label="Start Date"
              type="date"
              value={formik.values.start_date?.split("T")[0] || ""}
              onChange={formik.handleChange}
              error={
                formik.touched.start_date && Boolean(formik.errors.start_date)
              }
              helperText={formik.touched.start_date && formik.errors.start_date}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              name="end_date"
              label="End Date"
              type="date"
              value={formik.values.end_date?.split("T")[0] || ""}
              onChange={formik.handleChange}
              error={formik.touched.end_date && Boolean(formik.errors.end_date)}
              helperText={formik.touched.end_date && formik.errors.end_date}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              name="timeline"
              label="Status"
              select
              value={formik.values.timeline}
              onChange={formik.handleChange}
              error={formik.touched.timeline && Boolean(formik.errors.timeline)}
              helperText={formik.touched.timeline && formik.errors.timeline}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value || "empty"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="match_format"
              label="Match Format"
              select
              value={formik.values.match_format || ""}
              onChange={formik.handleChange}
            >
              {formatOptions.map((option) => (
                <MenuItem key={option.value || "empty"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="type"
              label="Tournament Type"
              select
              value={formik.values.type || ""}
              onChange={formik.handleChange}
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value || "empty"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="description"
              label="Description"
              value={formik.values.description || ""}
              onChange={formik.handleChange}
              multiline
              rows={2}
            />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                name="is_visible"
                label="Visible"
                select
                value={formik.values.is_visible ? "true" : "false"}
                onChange={(e) => {
                  formik.setFieldValue("is_visible", e.target.value === "true");
                }}
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </TextField>
              <TextField
                fullWidth
                margin="normal"
                name="priority"
                label="Priority"
                type="number"
                value={formik.values.priority}
                onChange={formik.handleChange}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                name="d_id"
                label="Dream11 ID"
                type="number"
                value={formik.values.d_id || ""}
                onChange={formik.handleChange}
              />
              <TextField
                fullWidth
                margin="normal"
                name="c_id"
                label="Circle ID"
                type="number"
                value={formik.values.c_id || ""}
                onChange={formik.handleChange}
              />
              <TextField
                fullWidth
                margin="normal"
                name="e_id"
                label="ESPN ID"
                type="number"
                value={formik.values.e_id || ""}
                onChange={formik.handleChange}
              />
              <TextField
                fullWidth
                margin="normal"
                name="mt_id"
                label="MT ID"
                type="number"
                value={formik.values.mt_id || ""}
                onChange={formik.handleChange}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedTournament ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Tournaments;
